import { ArrowUpRight, Instagram, Facebook, Twitter, Youtube, BookOpen, HelpCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { siteContent } from "@/content/site";

const { footer, brand } = siteContent;

const SOCIAL_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  youtube: Youtube,
};

export default function Footer() {
  const navigate = useNavigate();

  const goToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        const e = document.getElementById(id);
        if (e) e.scrollIntoView({ behavior: "smooth" });
      }, 120);
    }
  };

  return (
    <footer
      data-testid="footer-section"
      className="relative bg-[#1F4E5F] text-[#F4FAF8] pt-16 lg:pt-20 pb-8 overflow-hidden"
    >
      <div className="grain absolute inset-0 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 pb-12 border-b border-white/10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5">
              <img
                src={brand.logoUrl}
                alt={brand.logoAlt}
                className="w-12 h-12 rounded-2xl object-cover bg-white p-0.5"
              />
              <div className="leading-tight">
                <p className="font-display font-extrabold text-xl">{brand.name}</p>
                <p className="text-[10px] font-bold tracking-[0.18em] text-[#E8A33D] uppercase">
                  {brand.subname}
                </p>
              </div>
            </div>
            <p className="mt-5 text-[#F4FAF8]/70 text-[15px] leading-relaxed max-w-md">
              {footer.tagline}
            </p>
            <button
              data-testid="footer-cta-beta"
              onClick={() => goToSection("beta")}
              className="mt-7 inline-flex items-center gap-2 bg-[#E8A33D] hover:bg-[#C88A28] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:gap-3"
            >
              {footer.cta} <ArrowUpRight className="w-4 h-4" />
            </button>

            {footer.social?.length > 0 && (
              <div className="mt-8">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#E8A33D] mb-3">
                  {footer.socialTitle}
                </p>
                <div className="flex items-center gap-2.5" data-testid="footer-social">
                  {footer.social.map((s) => {
                    const Icon = SOCIAL_ICONS[s.key] || ArrowUpRight;
                    return (
                      <a
                        key={s.key}
                        data-testid={`footer-social-${s.key}`}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.label}
                        title={s.label}
                        className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#E8A33D] flex items-center justify-center text-[#F4FAF8] hover:text-white transition-colors"
                      >
                        <Icon className="w-4 h-4" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {footer.columns.map((c, i) => (
              <div key={i}>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#E8A33D] mb-4">
                  {c.title}
                </p>
                <ul className="space-y-2.5">
                  {c.links.map((l, k) => (
                    <li key={k}>
                      {c.type === "routes" ? (
                        <Link
                          data-testid={`footer-link-${l.id}`}
                          to={l.to}
                          className="text-[#F4FAF8]/70 hover:text-[#F4FAF8] text-sm transition-colors"
                        >
                          {l.l}
                        </Link>
                      ) : (
                        <button
                          data-testid={`footer-link-${l.id}`}
                          onClick={() => goToSection(l.id)}
                          className="text-[#F4FAF8]/70 hover:text-[#F4FAF8] text-sm transition-colors"
                        >
                          {l.l}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#E8A33D] mb-4">
                {footer.partnersTitle}
              </p>
              <ul className="space-y-2.5 text-[#F4FAF8]/70 text-sm">
                {footer.partnersItems.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="pt-7 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-xs text-[#F4FAF8]/55">
              © {new Date().getFullYear()} {footer.copyrightHolder}
            </p>
            <Link
              data-testid="footer-blog-link"
              to="/blog"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F4FAF8]/80 hover:text-[#E8A33D] transition-colors"
            >
              <BookOpen className="w-3.5 h-3.5" /> {footer.blogLabel}
            </Link>
            <Link
              data-testid="footer-faq-link"
              to="/faq"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F4FAF8]/80 hover:text-[#E8A33D] transition-colors"
            >
              <HelpCircle className="w-3.5 h-3.5" /> FAQ
            </Link>
          </div>
          <p className="text-xs text-[#F4FAF8]/55">{footer.signoff}</p>
        </div>
      </div>
    </footer>
  );
}
